
const ROOTS = {
  AUTH: '/auth',
  // AUTH_DEMO: '/auth-demo',
  // DASHBOARD: '/dashboard',
  SERVICE: '/service',
  HOME: '/',
  ADMIN: '/admin',
};

// ----------------------------------------------------------------------
// 서비스 구역 — 사이드바 구역 이름이 주소의 첫 마디다(`/service/<구역>/<화면>`)

/** DISCOVER — 무엇이 돌고 있나 훑는 허브 셋 + 북마크 */
const DISCOVER = {
  root: `${ROOTS.SERVICE}/discover`,
  /** 컨텐츠 허브 */
  contents: `${ROOTS.SERVICE}/discover/contents`,
  /**
   * 컨텐츠 허브를 **그 조건이 걸린 채로** 연다 — 검색어 · 탭(`all`·`ads`·`organic`·`products`) · 기간(일).
   * 🔑 그 화면이 들어올 때 한 번 받고 주소에서 걷는다(`readLinkedMediaState`). 빈 칸은 싣지 않는다.
   */
  contentsAt: ({ q = '', tab = '', days = 0 }: { q?: string; tab?: string; days?: number } = {}) => {
    const query = new URLSearchParams();
    if (q.trim()) query.set('q', q.trim());
    if (tab) query.set('tab', tab);
    if (days > 0) query.set('days', String(days));
    const text = query.toString();
    return `${ROOTS.SERVICE}/discover/contents${text ? `?${text}` : ''}`;
  },
  /** 브랜드 허브 — 상세는 `?brand=` */
  brands: `${ROOTS.SERVICE}/discover/brands`,
  /**
   * **다른 화면에서 브랜드 하나를 열 때** — 목록에서 좁혀 둔 조건을 빼고 연다.
   *
   * 🚨 브랜드 허브 목록의 조건(기간·제품군·채널·국가)은 세션에 남아 **상세에도 걸린다.**
   *   그대로 두면 크리에이터 허브의 「다룬 브랜드」 칩을 눌러 들어온 사람이 그 브랜드의 «일부»를
   *   전부로 읽는다 — 자기가 건 적 없는 조건이라 왜 적은지도 모른다.
   * ⚠️ 허브 «안»의 이동(목록 → 상세)은 이 길이 아니다 — 거기서는 조건이 이어져야 목록과 상세가
   *   안 어긋난다.
   */
  brandFresh: (brand: string) =>
    `${ROOTS.SERVICE}/discover/brands?brand=${encodeURIComponent(brand)}&scope=all`,
  /** 크리에이터 허브 */
  creators: `${ROOTS.SERVICE}/discover/creators`,
  /** 크리에이터 허브 상세 — 채널과 계정은 늘 한 짝이다(같은 아이디가 채널마다 다른 사람). 채널을 모르면 목록 */
  creatorDetail: (channel: string, account: string) =>
    channel.trim() && account.trim()
      ? `${ROOTS.SERVICE}/discover/creators?channel=${encodeURIComponent(channel.trim())}&account=${encodeURIComponent(account.trim())}`
      : `${ROOTS.SERVICE}/discover/creators`,
  /** 크리에이터 찾기 목록을 **그 정렬로** 연다(`recent30View` 등 · 목록의 다른 조건은 기본값으로) */
  creatorsSorted: (sort: string) =>
    sort.trim()
      ? `${ROOTS.SERVICE}/discover/creators?sort=${encodeURIComponent(sort.trim())}`
      : `${ROOTS.SERVICE}/discover/creators`,
  /** 크리에이터 찾기 목록을 **타겟 키워드가 걸린 채로** 연다(그 말을 쓴 계정으로 좁힌다) */
  creatorsKeyword: (keyword: string) =>
    keyword.trim()
      ? `${ROOTS.SERVICE}/discover/creators?keyword=${encodeURIComponent(keyword.trim())}`
      : `${ROOTS.SERVICE}/discover/creators`,
  bookmarks: {
    root: `${ROOTS.SERVICE}/discover/bookmarks`,
    folder: (id: string) => `${ROOTS.SERVICE}/discover/bookmarks/${id}`,
  },
};

/**
 * RESEARCH — 조건을 걸고 파고드는 조사 화면. 갈래(`social`·`ads`·`products`·`events`)마다 **제 주소**다 — 사이드바가 지금 보는
 * 갈래를 켤 수 있어야 한다(질의로는 안 켜진다). 갈래 없는 `root` 는 소셜 갈래로 넘어간다(`legacy-redirects`).
 */
const RESEARCH = {
  root: `${ROOTS.SERVICE}/research`,
  tab: (tab: string) => `${ROOTS.SERVICE}/research/${tab}`,
};

/** 추적 관리를 어디에 맞춰 여나 — 종류 칩 · 세트 편집 창 · 제외 키워드 창 · 인스타 연동 창 */
export type TrackingAtOptions = {
  kind?: 'brand' | 'keyword' | 'account';
  /** 이 세트의 편집 창을 연다 */
  editSet?: string | null;
  /** 우리 브랜드의 제외 키워드 창을 연다 */
  exclude?: boolean;
  /** 이 자사 계정의 인스타 연동 창을 연다(`instagram` 이 아닌 계정은 무시된다) */
  linkAccount?: string | null;
};

/** TRACKING — 걸어 두면 계속 쌓이는 것들. `root` 가 추적 관리다 */
const TRACKING = {
  root: `${ROOTS.SERVICE}/tracking`,
  /** 추적 관리를 그 자리에 맞춰 연다 — 옛 설정 탭(우리 브랜드·타겟 멘션)이 여기로 이어진다 */
  at: (opts: TrackingAtOptions = {}) => {
    const query = new URLSearchParams();
    if (opts.kind) query.set('kind', opts.kind);
    if (opts.editSet) query.set('editSet', String(opts.editSet));
    if (opts.exclude) query.set('exclude', '1');
    if (opts.linkAccount) query.set('link', String(opts.linkAccount));
    const qs = query.toString();
    return qs ? `${ROOTS.SERVICE}/tracking?${qs}` : `${ROOTS.SERVICE}/tracking`;
  },
  /** 브랜드 멘션 추적 — 남들이 우리를 말한 글 */
  brandMentions: `${ROOTS.SERVICE}/tracking/brand-mentions`,
  /** 대시보드를 꾸미는 전용 페이지 — 필터 사이드바 없이 전폭을 쓴다 */
  brandMentionsEdit: `${ROOTS.SERVICE}/tracking/brand-mentions/edit`,
  /** 타겟 멘션 추적 — 세트 하나는 `targetMentionSet(id)` */
  targetMentions: `${ROOTS.SERVICE}/tracking/target-mentions`,
  targetMentionSet: (id: string) => `${ROOTS.SERVICE}/tracking/target-mentions/${encodeURIComponent(id)}`,
  /** 검색량 트렌드 — 한 워크스페이스에만 서는 줄(`isSearchTrendFeatureWorkspace`) */
  searchTrend: `${ROOTS.SERVICE}/tracking/search-trend`,
};

/** OPERATIONS — 우리 것을 돌보는 자리 */
const OPERATIONS = {
  /** 자사 계정 관리 */
  accounts: `${ROOTS.SERVICE}/operations/accounts`,
  /** 시딩 전 체크 */
  seedingCheck: `${ROOTS.SERVICE}/operations/seeding-check`,
  /** 시딩 성과 모니터링 — 캠페인 상세는 `detail(id)` */
  seeding: {
    root: `${ROOTS.SERVICE}/operations/seeding`,
    detail: (id: string) => `${ROOTS.SERVICE}/operations/seeding/${id}`,
    /**
     * 시딩 화면에서 캠페인 창을 연다 — 만들기 · 고치기 · 복제.
     * @param campaignId 고치기·복제의 대상 — 만들기면 없다
     */
    editor: (mode: 'new' | 'edit' | 'duplicate', campaignId?: string | null) => {
      const query = new URLSearchParams({ editor: mode });
      if (mode !== 'new' && campaignId) query.set('campaignId', String(campaignId));
      return `${ROOTS.SERVICE}/operations/seeding?${query.toString()}`;
    },
  },
};

/**
 * LABS — 커스텀 줄(워크스페이스 `labs` 배열) + 아직 다듬는 것(심화분석·위젯 설정).
 * ⚠️ 플랜 관문이 줄마다 다르다 — `utils/plan/service-route-access` 가 긴 주소부터 본다.
 */
const LABS = {
  root: `${ROOTS.SERVICE}/labs`,
  /** 컨텐츠 플래너 */
  contentPlanner: `${ROOTS.SERVICE}/labs/content-planner`,
  /** 활동 관리 페이지 (channelId = Firestore platforms 문서 ID) */
  contentPlannerActivity: (personaId: string, channelId: string) =>
    `${ROOTS.SERVICE}/labs/content-planner/${personaId}/${channelId}`,
  /** 멘션 통계 */
  mentionStats: `${ROOTS.SERVICE}/labs/mention-stats`,
  /** 링크 추적 */
  linkTracking: `${ROOTS.SERVICE}/labs/link-tracking`,
  advancedSearch: `${ROOTS.SERVICE}/labs/advanced-search`,
  contentIdeation: `${ROOTS.SERVICE}/labs/content-ideation`,
  /** 심화분석(베타) */
  deepAnalysis: `${ROOTS.SERVICE}/labs/deep-analysis`,
  /** 위젯 설정 */
  widgets: `${ROOTS.SERVICE}/labs/widgets`,
  /** 컨텐츠 분석 — 분석 그룹 목록 · 그룹 하나는 `detail(id)` · `labs` 에 `LAB_POST_ANALYSIS` 가 있는 워크스페이스만 */
  postAnalysis: {
    root: `${ROOTS.SERVICE}/labs/post-analysis`,
    detail: (id: string) => `${ROOTS.SERVICE}/labs/post-analysis/${encodeURIComponent(id)}`,
    /** 그 시딩 캠페인의 글로 새 작업 만들기(목록에서 이름 창이 열린다) */
    fromCampaign: (campaignId: string, campaignName: string) =>
      `${ROOTS.SERVICE}/labs/post-analysis?${new URLSearchParams({ fromCampaign: campaignId, campaignName })}`,
    /** 목록의 그 폴더 — `null` 이면 최상위 */
    folder: (folderId: string | null) =>
      (folderId ? `${ROOTS.SERVICE}/labs/post-analysis?folder=${encodeURIComponent(folderId)}` : `${ROOTS.SERVICE}/labs/post-analysis`),
  },
};

// ----------------------------------------------------------------------

export const paths = {
  comingSoon: '/coming-soon',
  maintenance: '/maintenance',
  trend: '/trend',
  /** 사용 가이드 — 로그인 없이 열리는 공개 화면. 홈은 매뉴얼 목록, `/<id>` 는 매뉴얼 한 편(id 는 `constants/manual-catalog`) */
  guide: {
    root: '/guide',
    manual: (id: string) => `/guide/${id}`,
  },
  /** 업데이트 노트 — 로그인 없이 열리는 공개 화면. 홈은 노트 목록, `/<slug>` 는 노트 한 편 */
  updates: {
    root: '/updates',
    note: (slug: string) => `/updates/${slug}`,
  },
  /** 광고 랜딩 — 광고로 들어온 비회원이 보는 공개 화면. 페이지마다 따로 짓고 admin 이 공개 여부를 켜고 끈다(`constants/ad-pages`) */
  ads: {
    page: (slug: string) => `/ads/${slug}`,
  },
  /** 비회원 질문 대화 — 랜딩 검색칸의 말 하나에 답한다(`?q=` 는 질문 글자만 · 색인하지 않는다). 로그인한 사람은 서비스 홈 대화로 보낸다 */
  ask: '/ask',
  pricing: '/pricing',
  payment: '/payment',
  about: '/about-us',
  contact: '/contact-us',
  faqs: '/faqs',
  page403: '/error/403',
  page404: '/error/404',
  page500: '/error/500',
  components: '/admin/components',
  // docs: 'https://docs.minimals.cc/',
  // changelog: 'https://docs.minimals.cc/changelog/',
  // zoneStore: 'https://mui.com/store/items/zone-landing-page/',
  // minimalStore: 'https://mui.com/store/items/minimal-dashboard/',
  // freeUI: 'https://mui.com/store/items/minimal-dashboard-free/',
  // figmaUrl: 'https://www.figma.com/design/oAxS3CUFO0ou7rH2lTS8oI/%5BPreview%5D-Minimal-Web.v7.0.0',
  // product: {
  //   root: `/product`,
  //   checkout: `/product/checkout`,
  //   details: (id: string) => `/product/${id}`,
  //   demo: { details: `/product/${MOCK_ID}` },
  // },
  // post: {
  //   root: `/post`,
  //   details: (id: string) => `/post/${id}`,
  // },
  home: {
    root: `/`,
    /** 서비스 소개 — 공개 지면(랜딩 `/` 는 데이터 둘러보기) */
    intro: `/intro`,
    search: `/search`,
    influencer: `/influencer`,
    features: `/features`,
    /** AI 도구 연결(MCP) 소개 — 공개 지면 */
    mcp: `/mcp`,
    blog: `/post`,
    dashboard: `/dashboard`,
    trial: `/trial`,
    terms: {
      privacy: `/terms/privacy`,
      service: `/terms/service`,
      marketing: `/terms/marketing`,
    },
    expired: `/expired`,
  },
  // AUTH
  auth: {
    root: ROOTS.AUTH,
    login: `${ROOTS.AUTH}/login`,
    register: `${ROOTS.AUTH}/register`,
    forgotPassword: `${ROOTS.AUTH}/forgot-password`,
    contactVerification: `${ROOTS.AUTH}/contact-verification`,
  },
  /**
   * 서비스 화면 — **주소가 사이드바 구역을 따른다**(`/service/<구역>/<화면>`). 메뉴 정본은 `layouts/nav-config-service.tsx`.
   *
   * 🔑 구역별 묶음(`home` · `discover` · `research` · `tracking` · `operations` · `labs` · `settings`)이 정본이다.
   *   그 아래 납작한 이름(`searchAll` · `brand` · `status` …)은 **같은 값을 가리키는 별칭**이다 — 화면 코드가 그 이름으로
   *   부르고 있어 남긴다. 값을 고칠 때는 묶음 쪽만 고친다.
   * 🚨 옛 주소(`/service/brand-acc` · `/service/deep-search/…` 등)는 `src/routes/legacy-redirects.json` 이 308 로 보낸다 —
   *   여기서 주소를 바꾸면 그 표에 옛 주소 → 새 주소 한 줄을 더한다(밖에서 들어오는 링크가 거기로 산다).
   */
  service: {
    root: ROOTS.SERVICE,
    /** 홈 — 서비스 첫 화면. 뿌리 주소 그대로다(검색어 없이 열면 홈, `?q=` 면 「한 번에 찾기」 결과) */
    home: ROOTS.SERVICE,
    discover: DISCOVER,
    research: RESEARCH,
    tracking: TRACKING,
    operations: OPERATIONS,
    labs: LABS,

    trend: `${ROOTS.SERVICE}/trend`,
    trendReport: `${ROOTS.SERVICE}/trend`,
    weekly: `${ROOTS.SERVICE}/weekly`,
    /** 옛 주소 — 화면이 없고 `legacy-redirects` 가 Research 소셜 갈래로 보낸다 */
    search: `${ROOTS.SERVICE}/search`,
    /** 옛 대시보드 주소 — 화면이 없고 `legacy-redirects` 가 홈으로 보낸다 */
    dashboard: `${ROOTS.SERVICE}/dashboard`,
    trending: `${ROOTS.SERVICE}/idea?tab=keyword`,
    idea: `${ROOTS.SERVICE}/idea`,
    ideaTab: (tab = 'keyword') => `${ROOTS.SERVICE}/idea?tab=${tab}`,
    /** **한 번에 찾기** = 홈 — 말 하나로 어느 화면에서 무엇이 나오는지 먼저 보고 고르는 자리 */
    find: ROOTS.SERVICE,
    /** 홈의 대화 화면 — 첫 문장 질문이 한 번 쌓는다(뒤로 가기 = 홈 · 돌아오면 탭 기억으로 같은 대화가 선다) */
    findChat: `${ROOTS.SERVICE}?chat=1`,
    /** 빈 대화 화면 — 「무엇이 궁금하세요?」 · 입력칸 · 추천 질문(탭 기억을 되살리지 않는다 · 홈이 읽고 `?chat=1` 로 갈아 끼운다) */
    findChatFresh: `${ROOTS.SERVICE}?chat=new`,
    /**
     * 그 질문을 대화 화면에서 한 번 다시 묻는다 — 로그인한 랜딩 검색칸 · 비회원 질문 대화 · 비회원 홈 카드의 로그인 뒤 돌아갈 곳(홈이 읽고 지운다 · 읽기를 옮기는 `?ask=<id>` 와 다른 칸).
     * @param read 미리 정한 읽기(`toAskPresetParam`) — 있으면 길잡이 모델을 거치지 않고 그 읽기로 답한다
     */
    findAsk: (question: string, read = '') =>
      `${ROOTS.SERVICE}?question=${encodeURIComponent(question)}${read ? `&read=${encodeURIComponent(read)}` : ''}`,
    /** 시작하기 — 새 워크스페이스의 오너가 처음 한 번 보는 네 걸음(서비스 레이아웃이 보낸다) */
    start: `${ROOTS.SERVICE}/start`,
    /** 시작하기를 열되 ③ 지켜볼 대상의 입력칸에 그 말을 채워 둔다 — 검색 결과의 「계속 지켜볼까요?」 줄이 넘긴다 */
    startWatching: (word: string) => `${ROOTS.SERVICE}/start?watch=${encodeURIComponent(word)}`,
    /** 시작하기 ① 우리 브랜드를 그 이름으로 채워 연다(결과의 「우리 브랜드로 추적할까요?」) */
    startOwnBrand: (name: string) => `${ROOTS.SERVICE}/start?ownBrand=${encodeURIComponent(name)}`,
    /**
     * 그 말로 이미 찾아 둔 채로 홈을 연다 — 스냅샷의 칩이 이 길로 넘긴다.
     * @param entity 사전 추천에서 고른 항목(`brand:아로셀`) — 비면 싣지 않는다
     */
    findQuery: (query: string, entity = '') =>
      `${ROOTS.SERVICE}?q=${encodeURIComponent(query)}${entity ? `&entity=${encodeURIComponent(entity)}` : ''}`,
    searchAll: DISCOVER.contents,
    searchAllAt: DISCOVER.contentsAt,
    searchAllAnalysis: RESEARCH.root,
    searchAllAnalysisTab: RESEARCH.tab,
    searchAllChat: `${ROOTS.SERVICE}/ai-chat`,
    viewNew: `${ROOTS.SERVICE}/view-new`,
    brandHub: DISCOVER.brands,
    brandHubFresh: DISCOVER.brandFresh,
    creators: DISCOVER.creators,
    creatorDetail: DISCOVER.creatorDetail,
    creatorsSorted: DISCOVER.creatorsSorted,
    creatorsKeyword: DISCOVER.creatorsKeyword,
    jobs: `${ROOTS.SERVICE}/jobs`,
    influencer: `${ROOTS.SERVICE}/influencer`,
    thumbnail: `${ROOTS.SERVICE}/idea?tab=thumbnail`,
    status: TRACKING.root,
    /** 작업 관리 — 무엇을 메뉴에 세울지 카드로 정하는 자리(추적 관리와 같은 결) */
    taskManage: `${ROOTS.SERVICE}/task-manage`,
    keywordMonitoring: TRACKING.targetMentions,
    brand: TRACKING.brandMentions,
    brandDashboardEdit: TRACKING.brandMentionsEdit,
    searchTrend: TRACKING.searchTrend,
    /** 경쟁사 추적 · 오늘의 뷰티 인사이트 · 제품군 시장조사 — 메뉴에 걸리지 않은 화면이라 구역 주소 밖에 선다 */
    competitor: `${ROOTS.SERVICE}/competitor`,
    insight: `${ROOTS.SERVICE}/insight`,
    marketResearch: `${ROOTS.SERVICE}/market-research`,
    dataStats: LABS.deepAnalysis,
    account: OPERATIONS.accounts,
    externalDashboard: LABS.widgets,
    expired: `${ROOTS.SERVICE}/expired`,
    workspace: `${ROOTS.SERVICE}/workspace`,
    /** 설정 한 장 — `?tab=` 으로 곧장 보낸다(`SETTINGS_RAIL` 의 열쇠) */
    settings: `${ROOTS.SERVICE}/settings`,
    settingsTab: (tab: string) => `${ROOTS.SERVICE}/settings?tab=${tab}`,
    /**
     * 설정 한 장으로 — 모달이 받던 인자(서브탭 · 강조할 캠페인 · 새 캠페인)를 주소에 싣는다.
     * 🔑 **모달을 대신하는 유일한 길이다** — 화면마다 주소를 손으로 짓지 않는다.
     */
    settingsAt: (
      tab: string,
      opts?: {
        subTab?: string;
        campaignId?: string | null;
        newCampaign?: boolean;
        duplicateFrom?: string | null;
      },
    ) => {
      const query = new URLSearchParams({ tab });
      if (opts?.subTab) query.set('subTab', opts.subTab);
      if (opts?.campaignId) query.set('campaignId', String(opts.campaignId));
      if (opts?.newCampaign) query.set('newCampaign', '1');
      if (opts?.duplicateFrom) query.set('duplicateFrom', String(opts.duplicateFrom));
      return `${ROOTS.SERVICE}/settings?${query.toString()}`;
    },
    workspaceNew: `${ROOTS.SERVICE}/workspace/new`,
    postBenchmark: `${ROOTS.SERVICE}/post-benchmark`,
    campaign: OPERATIONS.seeding,
    request: `${ROOTS.SERVICE}/request`,
    bookmark: DISCOVER.bookmarks,
    influencerCheck: OPERATIONS.seedingCheck,
    /** Labs 의 커스텀 줄 — 워크스페이스 `labs` 배열로 켜지는 것들 */
    customService: {
      root: LABS.root,
      redditContent: LABS.contentPlanner,
      contentPlannerActivity: LABS.contentPlannerActivity,
      brandExcel: LABS.mentionStats,
      contentIdeation: LABS.contentIdeation,
      trackingLink: LABS.linkTracking,
    },
  },
  subscription: {
    root: `/subscription`,
    checkout: `/subscription/billing/checkout`,
    success: `/subscription/billing/success`,
    plan: `/subscription/plan`,
    cardChangeSuccess: `/subscription/card-change/success`,
    /** 유료 구독 중 플랜을 바꾸는 확인 판 — `?plan=<planId>&cycle=<monthly|yearly>` */
    planChangeCheckout: `/subscription/plan-change/checkout`,
    /** 그 결과 판 — `?plan=<planId>&kind=<upgrade|schedule>` */
    planChangeSuccess: `/subscription/plan-change/success`,
  },
  admin: {
    root: ROOTS.ADMIN,
    management: {
      root: `${ROOTS.ADMIN}/management`,
      setting: `${ROOTS.ADMIN}/management/setting`,
      screening: `${ROOTS.ADMIN}/management/screening`,
      stats: `${ROOTS.ADMIN}/management/stats`,
      subscription: `${ROOTS.ADMIN}/management/subscription`,
    },
    customer: {
      root: `${ROOTS.ADMIN}/customer`,
      activity: `${ROOTS.ADMIN}/customer/activity`,
      event: `${ROOTS.ADMIN}/customer/event`,
      dashboard: `${ROOTS.ADMIN}/customer/dashboard`,
      userList: `${ROOTS.ADMIN}/customer/user`,
      path: `${ROOTS.ADMIN}/customer/path`,
      workspace: `${ROOTS.ADMIN}/customer/workspace`,
    },
    system: {
      root: `${ROOTS.ADMIN}/system`,
      monitoring: `${ROOTS.ADMIN}/system/monitoring`,
      searchLog: `${ROOTS.ADMIN}/system/search-log`,
      aiScreening: `${ROOTS.ADMIN}/system/ai-screening`,
      hubcheck: `${ROOTS.ADMIN}/system/hubcheck`,
    },
    tools: {
      root: `${ROOTS.ADMIN}/tools`,
      pushData: `${ROOTS.ADMIN}/tools/push-data`,
      dataTransfer: `${ROOTS.ADMIN}/tools/data-transfer`,
      alimtalkCheck: `${ROOTS.ADMIN}/tools/alimtalk-check`,
    },
    /** 화면 mockup(매뉴얼·영업 메일 캡처용) — 목록은 `constants/mockup-catalog.ts` */
    mockup: {
      root: `${ROOTS.ADMIN}/mockup`,
      screen: (path: string) => `${ROOTS.ADMIN}/mockup/${path}`,
    },
    post: {
      root: `${ROOTS.ADMIN}/post`,
      new: `${ROOTS.ADMIN}/post/new`,
      details: (id: string) => `${ROOTS.ADMIN}/post/${id}`,
      edit: (id: string) => `${ROOTS.ADMIN}/post/${id}/edit`,
    },
  },
  post: {
    root: `${ROOTS.ADMIN}/post`,
    details: (id: string) => `/post/${id}`
  },

  // },
};
